import {
  Activity,
  ArrowLeft,
  Eye,
  Globe,
  Info,
  Package,
  Pencil,
  Plus,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import {
  Callout,
  Checkbox,
  CommerceCombobox,
  DataTable,
  DataTableFacetedFilter,
  DataTableToolbar,
  DescriptionText,
  Field,
  HorizontalStack,
  RegistryEmptyState,
  SectionHeading,
  Surface,
  VerticalStack,
  WizardWorkspace,
  type ColumnDef,
  type CommerceSelectOptionViewModel,
  type ColumnFiltersState,
  type RowSelectionState,
  type SortingState,
  type WizardTab,
  toast,
} from '@workspace/silk-ui';
import {
  buildEligibilityPolicyDisplayTitle,
  buildEligibilityPolicyScopeSummary,
  createEmptyEligibilityForm,
  formatEligibilitySubjectType,
  buildEligibilityFormFromPolicy,
  buildEligibilityPayload,
  createEligibilityPolicy,
  deleteEligibilityPolicy,
  getCommerceErrorMessage,
  listEligibilityPolicies,
  updateEligibilityPolicy,
  type CatalogPublication,
  type EligibilityPolicy,
  type StorefrontExperience,
  describeEligibilitySubjectType,
  describeEligibilitySubjectSelection,
  formatDate,
} from '@/lib/commerce';
import { cn } from '@/lib/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCommerceRegistryQuery } from '@/hooks/useCommerceRegistryQuery';
import { defineCommerceRegistryColumns } from '@/lib/commerceRegistryColumns';

const ELIGIBILITY_RULE_SORT_FIELDS = ['action', 'created_at', 'effective_status', 'policy_label', 'priority', 'subject_type', 'updated_at'] as const;
import { lastPageIndexForTotal, toLimitOffset } from '@/lib/routePagination';
import { StorefrontPolicySentence } from './StorefrontPolicySentence';
import { CommerceRegistryUtilityBand } from './catalogHierarchyShared';

const inputClass = 'h-11 w-full rounded-lg border border-border/60 bg-background px-3 text-sm font-medium text-foreground outline-none transition-all focus:border-primary/40 focus:ring-4 focus:ring-primary/5';

type PolicyLookups = {
  product: Map<string, CommerceSelectOptionViewModel>;
  variant: Map<string, CommerceSelectOptionViewModel>;
  serviceClass: Map<string, CommerceSelectOptionViewModel>;
  serviceGroup: Map<string, CommerceSelectOptionViewModel>;
  market: Map<string, CommerceSelectOptionViewModel>;
  segment: Map<string, CommerceSelectOptionViewModel>;
  tenant: Map<string, CommerceSelectOptionViewModel>;
  store: Map<string, CommerceSelectOptionViewModel>;
  agent: Map<string, CommerceSelectOptionViewModel>;
};

// ---------------------------------------------------------------------------
// EligibilityRulesPanel — rules table shown outside any wizard
// ---------------------------------------------------------------------------

export function EligibilityRulesPanel({
  experience,
  policyLookups,
  openPreview,
  onBack,
  onCreateRule,
  onEditRule,
}: {
  experience: StorefrontExperience;
  policyLookups: PolicyLookups;
  openPreview: (experience: StorefrontExperience) => void;
  onBack: () => void;
  onCreateRule: () => void;
  onEditRule: (policy: EligibilityPolicy) => void;
}) {
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState<string | null>(null);
  const [eligibilityPolicies, setEligibilityPolicies] = useState<EligibilityPolicy[]>([]);
  const query = useCommerceRegistryQuery({
    facetKeys: ['action', 'subject_type'],
    facetValues: {
      action: ['allow', 'deny'],
      subject_type: ['catalog', 'product', 'product_key', 'product_type', 'service_class', 'service_group', 'variant', 'variant_key'],
    },
    sortFields: ELIGIBILITY_RULE_SORT_FIELDS,
  });
  const rulesSearchQuery = query.search;
  const sorting = useMemo<SortingState>(() => query.sort.map(token => ({
    id: token.startsWith('-') ? token.slice(1) : token,
    desc: token.startsWith('-'),
  })), [query.sort]);
  const [totalPolicies, setTotalPolicies] = useState(0);
  const [facetCounts, setFacetCounts] = useState<Record<string, Record<string, number>>>({});
  const requestSequence = useRef(0);
  const rulesColumnFilters = useMemo<ColumnFiltersState>(() => [
    ...(query.facets.action.length > 0 ? [{ id: 'action', value: query.facets.action }] : []),
    ...(query.facets.subject_type.length > 0 ? [{ id: 'subject_type', value: query.facets.subject_type }] : []),
  ], [query.facets.action, query.facets.subject_type]);
  const [rulesRowSelection, setRulesRowSelection] = useState<RowSelectionState>({});
  const handleBack = useCallback(() => {
    query.clear();
    onBack();
  }, [onBack, query.clear]);

  const loadRules = useCallback(async () => {
    const requestId = ++requestSequence.current;
    if (!experience?.publication_id) {
      setEligibilityPolicies([]);
      setTotalPolicies(0);
      setFacetCounts({});
      return;
    }
    setRulesLoading(true);
    setRulesError(null);
    try {
      const data = await listEligibilityPolicies({
        publication_id: experience.publication_id,
        search: rulesSearchQuery || undefined,
        action: query.facets.action,
        subject_type: query.facets.subject_type,
        sort: query.sort,
        include_facets: ['action', 'subject_type'],
        ...toLimitOffset(query.pagination),
      });
      if (requestId !== requestSequence.current) return;
      const nextPageIndex = lastPageIndexForTotal(data.total_count, query.pagination.pageSize);
      if (query.pagination.pageIndex > nextPageIndex) {
        query.onPaginationChange(current => ({ ...current, pageIndex: nextPageIndex }));
        return;
      }
      setEligibilityPolicies(data.items);
      setTotalPolicies(data.total_count);
      setFacetCounts(Object.fromEntries(Object.entries(data.facets).map(([key, buckets]) => [
        key,
        Object.fromEntries(buckets.map(bucket => [bucket.value, bucket.count])),
      ])));
    } catch (err) {
      if (requestId !== requestSequence.current) return;
      setEligibilityPolicies([]);
      setTotalPolicies(0);
      setFacetCounts({});
      setRulesError(getCommerceErrorMessage(err, 'Failed to load storefront eligibility rules.'));
    } finally {
      if (requestId === requestSequence.current) setRulesLoading(false);
    }
  }, [experience?.publication_id, query.facets.action, query.facets.subject_type, query.pagination, query.sort, rulesSearchQuery]);

  useEffect(() => {
    void loadRules();
  }, [loadRules]);

  const handleDeleteRule = useCallback(async (policy: EligibilityPolicy) => {
    const label = buildEligibilityPolicyDisplayTitle(policy, policyLookups) || policy.policy_id;
    if (!window.confirm(`Delete eligibility rule "${label}"?`)) return;
    setRulesError(null);
    try {
      await deleteEligibilityPolicy(policy.policy_id);
      await loadRules();
      toast.success('Eligibility rule deleted', { description: label });
    } catch (err) {
      const message = getCommerceErrorMessage(err, 'Failed to delete eligibility rule.');
      setRulesError(message);
      toast.error('Delete failed', { description: message });
    }
  }, [loadRules, policyLookups]);

  const columns = useMemo<ColumnDef<EligibilityPolicy>[]>(() => [
    {
      id: 'select',
      size: 40,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={value => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={value => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: 'policy_label',
      size: 340,
      accessorFn: row => buildEligibilityPolicyDisplayTitle(row, policyLookups),
      header: 'Policy',
      cell: ({ row }) => {
        const policy = row.original;
        return (
          <button type="button" onClick={() => onEditRule(policy)} className="group min-w-0 text-left">
            <p className="text-sm font-black text-foreground transition-colors group-hover:text-primary">
              {buildEligibilityPolicyDisplayTitle(policy, policyLookups)}
            </p>
            <DescriptionText className="line-clamp-1 text-[11px]">
              {buildEligibilityPolicyScopeSummary(policy, policyLookups)}
            </DescriptionText>
          </button>
        );
      },
    },
    {
      accessorKey: 'action',
      size: 90,
      header: 'Action',
      cell: ({ row }) => (
        <span
          className={cn(
            'rounded px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.2em] text-white',
            row.original.action === 'allow' ? 'bg-emerald-600' : 'bg-rose-600',
          )}
        >
          {row.original.action}
        </span>
      ),
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      id: 'subject_type',
      size: 140,
      accessorFn: row => formatEligibilitySubjectType(row.subject_type),
      header: 'Target Type',
      cell: ({ row }) => (
        <span className="rounded border border-border/50 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">
          {formatEligibilitySubjectType(row.original.subject_type)}
        </span>
      ),
    },
    {
      accessorKey: 'effective_status',
      size: 110,
      header: 'Status',
      cell: ({ row }) => (
        <span className="rounded border border-border/50 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">
          {row.original.effective_status || 'active'}
        </span>
      ),
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      accessorKey: 'priority',
      size: 80,
      header: 'Priority',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">{row.original.priority}</span>
      ),
    },
    {
      accessorKey: 'updated_at',
      size: 130,
      header: 'Updated',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">{formatDate(row.original.updated_at)}</span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      size: 90,
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        const policy = row.original;
        return (
          <HorizontalStack gap={2} className="justify-end shrink-0">
            <button
              type="button"
              onClick={() => onEditRule(policy)}
              aria-label={`Edit ${policy.policy_label || policy.policy_id}`}
              className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
            >
              <Pencil className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => void handleDeleteRule(policy)}
              aria-label={`Delete ${policy.policy_label || policy.policy_id}`}
              className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-destructive/40 hover:text-destructive hover:bg-destructive/5"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </HorizontalStack>
        );
      },
    },
  ], [handleDeleteRule, onEditRule, policyLookups]);
  defineCommerceRegistryColumns(columns, { mode: 'manual', sortFields: ELIGIBILITY_RULE_SORT_FIELDS, registry: 'eligibility-rules' });

  return (
    <div className="relative z-50 flex h-full max-h-full min-h-0 flex-1 flex-col overflow-hidden bg-background animate-in fade-in duration-300">
      <header className="shrink-0 border-b border-border/60 bg-muted/5 px-4 py-2 sm:px-6 sm:py-3">
        <div className="flex min-w-0 items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-4">
            <button
              type="button"
              onClick={handleBack}
              className="group flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border/60 transition-all hover:bg-muted"
            >
              <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
            </button>
            <div className="min-w-0">
              <h1 className="truncate text-base font-black tracking-tight sm:text-xl">
                Eligibility Rules: {experience.name}
              </h1>
              <p className="hidden truncate text-[10px] font-bold text-muted-foreground/80 sm:block">
                Manage rules controlling sellability for this storefront
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
            <button
              type="button"
              onClick={() => openPreview(experience)}
              className="hidden items-center gap-2 rounded-md border border-border/50 px-3 py-1.5 text-xs font-black text-foreground transition hover:bg-muted/30 sm:flex"
            >
              <Eye className="h-3.5 w-3.5" />
              Preview
            </button>
            <button
              type="button"
              onClick={onCreateRule}
              disabled={!experience?.publication_id}
              className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-black text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:opacity-90 disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">New Rule</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {!experience?.publication_id ? (
          <div className="flex flex-1 items-center justify-center p-6">
            <RegistryEmptyState
              icon={<ShieldCheck className="h-6 w-6" />}
              title="This storefront is missing a publication"
              description="Link or auto-create the storefront publication first, then return here to manage the policies that control sellability."
            />
          </div>
        ) : (
          <>
            {rulesError ? (
              <div className="px-4 pt-4 sm:px-6">
                <Callout variant="error">{rulesError}</Callout>
              </div>
            ) : null}

            <CommerceRegistryUtilityBand>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
                  <div className="inline-flex min-h-10 items-center rounded-md border border-border/50 bg-muted/20 px-3 text-xs font-black text-foreground">
                    {experience.name}
                  </div>
                </div>
              </div>
            </CommerceRegistryUtilityBand>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <DataTable
                mode="manual"
                columns={columns}
                data={eligibilityPolicies}
                sorting={sorting}
                onSortingChange={updater => {
                  const next = typeof updater === 'function' ? updater(sorting) : updater;
                  query.setSort(next.map(item => `${item.desc ? '-' : ''}${item.id}`));
                }}
                globalFilter={rulesSearchQuery}
                columnFilters={rulesColumnFilters}
                onColumnFiltersChange={updater => {
                  const next = typeof updater === 'function' ? updater(rulesColumnFilters) : updater;
                  if (next.length === 0 && rulesColumnFilters.length > 0) {
                    query.clear();
                    return;
                  }
                  for (const key of ['action', 'subject_type']) {
                    const filter = next.find(item => item.id === key);
                    const values = Array.isArray(filter?.value) ? filter.value.map(String) : [];
                    if (values.join('\u0000') !== query.facets[key].join('\u0000')) {
                      query.setFacet(key, values);
                      return;
                    }
                  }
                }}
                rowSelection={rulesRowSelection}
                onRowSelectionChange={setRulesRowSelection}
                pagination={query.pagination}
                onPaginationChange={query.onPaginationChange}
                manualPagination
                totalRowCount={totalPolicies}
                className="border-none rounded-none"
                paginationClassName="px-0 py-0"
                showPagination={true}
                loading={rulesLoading}
                emptyState={
                  <div className="px-0 py-12 text-center">
                    <p className="text-sm font-black text-foreground">No eligibility rules yet</p>
                    <DescriptionText className="mt-1 text-xs">
                      {rulesSearchQuery || query.facets.action.length > 0 || query.facets.subject_type.length > 0
                        ? 'No storefront rules matched the current search or filters.'
                        : 'Create the first rule to control what this storefront can sell by market, segment, store, or product.'}
                    </DescriptionText>
                    {rulesSearchQuery || query.facets.action.length > 0 || query.facets.subject_type.length > 0 ? (
                      <button type="button" onClick={query.clear} className="mt-3 text-xs font-semibold text-primary hover:underline">
                        Reset search and filters
                      </button>
                    ) : null}
                  </div>
                }
                renderToolbar={table => (
                  <DataTableToolbar
                    table={table}
                    searchValue={rulesSearchQuery}
                    onSearchChange={query.setSearch}
                    searchPlaceholder="Search storefront rules..."
                    resultCount={totalPolicies}
                    className="border-b border-border/40 bg-transparent px-0 py-0"
                    filters={
                      <HorizontalStack gap={2}>
                        {table.getColumn('action') ? (
                          <DataTableFacetedFilter
                            title="Action"
                            selectedValues={new Set(query.facets.action)}
                            onFilterChange={values => query.setFacet('action', Array.from(values))}
                            counts={facetCounts.action}
                            options={[
                              { label: 'Allow', value: 'allow' },
                              { label: 'Deny', value: 'deny' },
                            ]}
                          />
                        ) : null}
                        {table.getColumn('subject_type') ? (
                          <DataTableFacetedFilter
                            title="Target Type"
                            selectedValues={new Set(query.facets.subject_type)}
                            onFilterChange={values => query.setFacet('subject_type', Array.from(values))}
                            counts={facetCounts.subject_type}
                            options={[
                              { label: 'Catalog', value: 'catalog' },
                              { label: 'Product', value: 'product' },
                              { label: 'Variant', value: 'variant' },
                              { label: 'Service Class', value: 'service_class' },
                              { label: 'Service Group', value: 'service_group' },
                            ]}
                          />
                        ) : null}
                      </HorizontalStack>
                    }
                  />
                )}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EligibilityRuleEditorWizard — WizardWorkspace for creating / editing a rule
// ---------------------------------------------------------------------------

const editorTabs: WizardTab[] = [
  { id: 'objective', label: 'Objective', icon: <Package className="h-4 w-4" /> },
  { id: 'context', label: 'Context', icon: <Globe className="h-4 w-4" /> },
  { id: 'logic', label: 'Logic', icon: <Activity className="h-4 w-4" /> },
  { id: 'advanced', label: 'Advanced', icon: <Info className="h-4 w-4" /> },
];

export function EligibilityRuleEditorWizard({
  isOpen,
  onClose,
  experience,
  editingPolicy,
  policyLookups,
  publicationLookup,
  publicationMap,
  marketOptions,
  segmentOptions,
  sellingTenantOptions,
  storeOptions,
  agentOptions,
  productOptions,
  variantOptions,
  serviceClassOptions,
  serviceGroupOptions,
  openPreview,
  onSaved,
}: {
  isOpen: boolean;
  onClose: () => void;
  experience: StorefrontExperience | null;
  editingPolicy: EligibilityPolicy | null;
  policyLookups: PolicyLookups;
  publicationLookup: Map<string, CommerceSelectOptionViewModel>;
  publicationMap: Map<string, CatalogPublication>;
  marketOptions: CommerceSelectOptionViewModel[];
  segmentOptions: CommerceSelectOptionViewModel[];
  sellingTenantOptions: CommerceSelectOptionViewModel[];
  storeOptions: CommerceSelectOptionViewModel[];
  agentOptions: CommerceSelectOptionViewModel[];
  productOptions: CommerceSelectOptionViewModel[];
  variantOptions: CommerceSelectOptionViewModel[];
  serviceClassOptions: CommerceSelectOptionViewModel[];
  serviceGroupOptions: CommerceSelectOptionViewModel[];
  openPreview: (experience: StorefrontExperience) => void;
  onSaved: () => void;
}) {
  const isEditing = Boolean(editingPolicy);
  const [stepTab, setStepTab] = useState('objective');
  const [ruleForm, setRuleForm] = useState(() =>
    editingPolicy
      ? buildEligibilityFormFromPolicy(editingPolicy)
      : createEmptyEligibilityForm(experience?.publication_id || ''),
  );
  const [ruleSaving, setRuleSaving] = useState(false);
  const [rulesError, setRulesError] = useState<string | null>(null);

  const subjectSelectorConfig = useMemo((): {
    mode: 'none' | 'subject_id' | 'subject_key' | 'custom_key' | 'custom';
    options: CommerceSelectOptionViewModel[];
    placeholder: string;
    emptyMessage: string;
  } => {
    switch (ruleForm.subjectType) {
      case 'product':
        return { mode: 'subject_id' as const, options: productOptions, placeholder: 'Select product', emptyMessage: 'No products found.' };
      case 'variant':
        return { mode: 'subject_id' as const, options: variantOptions, placeholder: 'Select variant', emptyMessage: 'No variants found.' };
      case 'service_class':
        return { mode: 'subject_id' as const, options: serviceClassOptions, placeholder: 'Select service class', emptyMessage: 'No service classes found.' };
      case 'service_group':
        return { mode: 'subject_id' as const, options: serviceGroupOptions, placeholder: 'Select service group', emptyMessage: 'No service groups found.' };
      case 'product_key':
        return { mode: 'subject_key' as const, options: [] as CommerceSelectOptionViewModel[], placeholder: 'Enter product key', emptyMessage: '' };
      case 'variant_key':
        return { mode: 'subject_key' as const, options: [] as CommerceSelectOptionViewModel[], placeholder: 'Enter variant key', emptyMessage: '' };
      case 'product_type':
        return { mode: 'custom_key' as const, options: [] as CommerceSelectOptionViewModel[], placeholder: 'Enter product type identifier', emptyMessage: '' };
      default:
        return { mode: 'none' as const, options: [] as CommerceSelectOptionViewModel[], placeholder: 'Catalog-wide policy', emptyMessage: 'Catalog-wide policies apply to the full publication scope.' };
    }
  }, [ruleForm.subjectType, productOptions, variantOptions, serviceClassOptions, serviceGroupOptions]);

  const handleSave = async () => {
    if (!experience?.publication_id) {
      setRulesError('This storefront needs a linked publication before eligibility rules can be saved.');
      return;
    }
    setRuleSaving(true);
    setRulesError(null);
    try {
      const payload = buildEligibilityPayload(ruleForm, experience.publication_id);
      if (isEditing && editingPolicy) {
        await updateEligibilityPolicy(editingPolicy.policy_id, payload);
      } else {
        await createEligibilityPolicy(payload);
      }
      toast.success(isEditing ? 'Eligibility rule updated' : 'Eligibility rule created', {
        description: experience.name,
      });
      onSaved();
    } catch (err) {
      const message = getCommerceErrorMessage(err, 'Failed to save eligibility rule.');
      setRulesError(message);
      toast.error('Save failed', { description: message });
    } finally {
      setRuleSaving(false);
    }
  };

  return (
    <WizardWorkspace
      isOpen={isOpen}
      isSaving={ruleSaving}
      onClose={onClose}
      title={isEditing ? `Edit Rule: ${experience?.name || ''}` : `New Rule: ${experience?.name || ''}`}
      subtitle="Define the subject, scope, and timing for this eligibility policy"
      tabs={editorTabs}
      activeTabId={stepTab}
      onTabChange={setStepTab}
      saveLabel={isEditing ? 'Save Rule' : 'Create Rule'}
      onSave={() => void handleSave()}
      discardLabel="Back to Rules"
    >
      <VerticalStack gap={8}>
        <StorefrontPolicySentence
          form={ruleForm}
          lookups={{
            ...policyLookups,
            publication: publicationLookup,
          }}
        />

        {rulesError ? (
          <Callout variant="error">{rulesError}</Callout>
        ) : null}

        {stepTab === 'objective' ? (
          <section>
            <SectionHeading title="Policy Target" subtitle="Define what this storefront rule applies to and what action it should take." />
            <Surface className="mt-4 border-border/60 p-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Field label="Policy Label">
                  <input
                    value={ruleForm.policyLabel}
                    onChange={event => setRuleForm(current => ({ ...current, policyLabel: event.target.value }))}
                    placeholder="Optional operator label"
                    className={inputClass}
                  />
                </Field>
                <Field label="Subject Type" description={describeEligibilitySubjectType(ruleForm.subjectType)}>
                  <select
                    value={ruleForm.subjectType}
                    onChange={event =>
                      setRuleForm(current => ({
                        ...current,
                        subjectType: event.target.value,
                        subjectId: '',
                        subjectKey: '',
                      }))
                    }
                    className={inputClass}
                  >
                    <option value="catalog">Catalog / Publication Wide</option>
                    <option value="product">Product</option>
                    <option value="variant">Variant</option>
                    <option value="service_class">Service Class</option>
                    <option value="service_group">Service Group</option>
                    <option value="product_key">Product Key</option>
                    <option value="variant_key">Variant Key</option>
                    <option value="product_type">Product Type</option>
                  </select>
                </Field>
                <Field label="Action">
                  <select
                    value={ruleForm.action}
                    onChange={event => setRuleForm(current => ({ ...current, action: event.target.value }))}
                    className={inputClass}
                  >
                    <option value="allow">Allow</option>
                    <option value="deny">Deny</option>
                  </select>
                </Field>
                <Field
                  label={
                    subjectSelectorConfig.mode === 'subject_key'
                      ? 'Subject Key'
                      : subjectSelectorConfig.mode === 'none'
                        ? 'Subject Scope'
                        : 'Subject'
                  }
                  description={describeEligibilitySubjectSelection(ruleForm.subjectType)}
                >
                  {subjectSelectorConfig.mode === 'none' ? (
                    <DescriptionText className="rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-xs">
                      This policy applies across the matched storefront publication and commercial scope.
                    </DescriptionText>
                  ) : subjectSelectorConfig.mode === 'custom_key' ? (
                    <input
                      value={ruleForm.subjectKey}
                      onChange={event => setRuleForm(current => ({ ...current, subjectKey: event.target.value }))}
                      placeholder={subjectSelectorConfig.placeholder}
                      className={inputClass}
                    />
                  ) : subjectSelectorConfig.mode === 'custom' ? (
                    <input
                      value={ruleForm.subjectId}
                      onChange={event => setRuleForm(current => ({ ...current, subjectId: event.target.value }))}
                      placeholder="Advanced custom subject identifier"
                      className={inputClass}
                    />
                  ) : (
                    <CommerceCombobox
                      options={subjectSelectorConfig.options}
                      value={subjectSelectorConfig.mode === 'subject_key' ? ruleForm.subjectKey : ruleForm.subjectId}
                      onChange={nextValue =>
                        setRuleForm(current => ({
                          ...current,
                          subjectId: subjectSelectorConfig.mode === 'subject_id' ? nextValue : '',
                          subjectKey: subjectSelectorConfig.mode === 'subject_key' ? nextValue : '',
                        }))
                      }
                      placeholder={subjectSelectorConfig.placeholder}
                      searchPlaceholder={`Search ${subjectSelectorConfig.placeholder.toLowerCase()}...`}
                      emptyMessage={subjectSelectorConfig.emptyMessage}
                    />
                  )}
                </Field>
              </div>
            </Surface>
          </section>
        ) : null}

        {stepTab === 'context' ? (
          <section>
            <SectionHeading title="Commercial Scope" subtitle="Define where and for whom this policy is active." />
            <Surface className="mt-4 border-border/60 p-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Field label="Publication">
                  <DescriptionText className="rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-xs">
                    {experience?.publication_id
                      ? (publicationMap.get(experience.publication_id)?.storefront_key || experience.publication_id)
                      : 'No publication linked'}
                  </DescriptionText>
                </Field>
                <Field label="Market">
                  <CommerceCombobox
                    options={marketOptions}
                    value={ruleForm.marketId}
                    onChange={marketId => setRuleForm(current => ({ ...current, marketId }))}
                    placeholder="Optional market scope"
                    searchPlaceholder="Search markets..."
                    emptyMessage="No markets are available yet."
                  />
                </Field>
                <Field label="Market Group">
                  <CommerceCombobox
                    options={segmentOptions}
                    value={ruleForm.segmentId}
                    onChange={segmentId => setRuleForm(current => ({ ...current, segmentId }))}
                    placeholder="Optional Market Group scope"
                    searchPlaceholder="Search Market Groups..."
                    emptyMessage="No Market Groups are available yet."
                  />
                </Field>
                <Field label="Selling Tenant">
                  <CommerceCombobox
                    options={sellingTenantOptions}
                    value={ruleForm.sellingTenantId}
                    onChange={sellingTenantId => setRuleForm(current => ({ ...current, sellingTenantId }))}
                    placeholder="Optional tenant scope"
                    searchPlaceholder="Search selling tenants..."
                    emptyMessage="No selling tenants are available yet."
                  />
                </Field>
                <Field label="Store">
                  <CommerceCombobox
                    options={storeOptions}
                    value={ruleForm.storeId}
                    onChange={storeId => setRuleForm(current => ({ ...current, storeId }))}
                    placeholder="Optional store scope"
                    searchPlaceholder="Search stores..."
                    emptyMessage="No stores are available yet."
                  />
                </Field>
                <Field label="Agent">
                  <CommerceCombobox
                    options={agentOptions}
                    value={ruleForm.agentUserId}
                    onChange={agentUserId => setRuleForm(current => ({ ...current, agentUserId }))}
                    placeholder="Optional agent scope"
                    searchPlaceholder="Search agents..."
                    emptyMessage="No agents are available yet."
                  />
                </Field>
              </div>
            </Surface>
          </section>
        ) : null}

        {stepTab === 'logic' ? (
          <section>
            <SectionHeading title="Timing & Requirements" subtitle="Set when the policy is active and any required pre-conditions." />
            <Surface className="mt-4 border-border/60 p-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Field label="Starts At">
                  <input
                    type="datetime-local"
                    value={ruleForm.startsAt}
                    onChange={event => setRuleForm(current => ({ ...current, startsAt: event.target.value }))}
                    className={inputClass}
                  />
                </Field>
                <Field label="Ends At">
                  <input
                    type="datetime-local"
                    value={ruleForm.endsAt}
                    onChange={event => setRuleForm(current => ({ ...current, endsAt: event.target.value }))}
                    className={inputClass}
                  />
                </Field>
                <Field label="Priority">
                  <input
                    type="number"
                    min="0"
                    value={ruleForm.priority}
                    onChange={event => setRuleForm(current => ({ ...current, priority: event.target.value }))}
                    className={inputClass}
                  />
                </Field>
                <Field label="Signup Requirement">
                  <select
                    value={ruleForm.requiresCanSignUp ? 'required' : 'optional'}
                    onChange={event =>
                      setRuleForm(current => ({ ...current, requiresCanSignUp: event.target.value === 'required' }))
                    }
                    className={inputClass}
                  >
                    <option value="optional">No requirement</option>
                    <option value="required">Must be eligible to sign up</option>
                  </select>
                </Field>
                <Field label="Install Scheduling Requirement">
                  <select
                    value={ruleForm.requiresCanScheduleInstall ? 'required' : 'optional'}
                    onChange={event =>
                      setRuleForm(current => ({
                        ...current,
                        requiresCanScheduleInstall: event.target.value === 'required',
                      }))
                    }
                    className={inputClass}
                  >
                    <option value="optional">No requirement</option>
                    <option value="required">Must be able to schedule install</option>
                  </select>
                </Field>
              </div>
            </Surface>
          </section>
        ) : null}

        {stepTab === 'advanced' ? (
          <section>
            <SectionHeading title="Metadata & Advanced" subtitle="Technical overrides and downstream integration data." />
            <Surface className="mt-4 border-border/60 p-6">
              <VerticalStack gap={6}>
                <Field label="Advanced Subject Key">
                  <input
                    value={subjectSelectorConfig.mode === 'custom' ? ruleForm.subjectKey : ''}
                    onChange={event => setRuleForm(current => ({ ...current, subjectKey: event.target.value }))}
                    disabled={subjectSelectorConfig.mode !== 'custom'}
                    placeholder="Used only for custom subject types"
                    className={cn(inputClass, 'disabled:cursor-not-allowed disabled:opacity-60')}
                  />
                </Field>
                <Field label="Metadata JSON">
                  <textarea
                    value={ruleForm.metadataJSON}
                    onChange={event => setRuleForm(current => ({ ...current, metadataJSON: event.target.value }))}
                    rows={10}
                    className="min-h-[220px] w-full rounded-lg border border-border/60 bg-background px-3 py-3 font-mono text-xs text-foreground outline-none transition-all focus:border-primary/40 focus:ring-4 focus:ring-primary/5"
                  />
                </Field>
              </VerticalStack>
            </Surface>
          </section>
        ) : null}

        {experience ? (
          <HorizontalStack gap={2} className="justify-end">
            <button
              type="button"
              onClick={() => openPreview(experience)}
              className="inline-flex items-center gap-2 rounded-md border border-border/50 px-3 py-2 text-xs font-black text-foreground transition hover:bg-muted/30"
            >
              <Eye className="h-3.5 w-3.5" />
              Preview Storefront
            </button>
          </HorizontalStack>
        ) : null}
      </VerticalStack>
    </WizardWorkspace>
  );
}
